import { LoginMethod } from "@/types/auth";
import React from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "./types";

export const LoginMethodStep: React.FC<StepComponentProps> = ({
  formData,
  onUpdateField,
  control,
}) => {
  const { t } = useTranslation();

  const handleLoginMethod = (method: LoginMethod) => {
    onUpdateField("loginMethod", method);
    // Clear other login fields
    if (method !== "email") onUpdateField("email", "");
    if (method !== "phone") onUpdateField("phone", "");
    if (method !== "aadhar") onUpdateField("aadhar", "");
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="text-center mb-3">
        <div className="inline-block px-4 py-2 bg-gradient-to-r from-primary/10 to-pink-100 dark:from-primary/20 dark:to-pink-900/20 rounded-lg border border-primary/20 dark:border-primary/30">
          <span className="text-sm font-semibold text-primary dark:text-primary">
            {t("auth.registration.welcomeChooseLoginMethod", {
              name: formData.name,
              defaultValue: `स्वागत है ${formData.name}, कृपया अपनी लॉगिन विधि चुनें`,
            })}
          </span>
        </div>
      </div>
      <div className="flex gap-2 mb-3 justify-center">
        {["email", "phone", "aadhar"].map((method) => (
          <button
            type="button"
            key={method}
            className={`px-4 py-2 rounded-lg border font-medium transition-all duration-300 min-w-[80px] text-sm ${
              formData.loginMethod === method
                ? "bg-primary text-white border-primary shadow-md transform scale-105"
                : "bg-[var(--bg-card)] text-primary border-primary/60 hover:border-primary hover:shadow-sm hover:scale-102"
            }`}
            onClick={() => handleLoginMethod(method as LoginMethod)}
          >
            {method === "email"
              ? "ईमेल"
              : method === "phone"
              ? "फ़ोन"
              : method === "aadhar"
              ? "आधार"
              : method}
          </button>
        ))}
      </div>
      {formData.loginMethod === "email" && (
        <div className="relative">
          <Controller
            name="email"
            control={control}
            rules={{
              required:
                t("auth.registration.emailRequired") || "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message:
                  t("auth.registration.invalidEmail") ||
                  "Please enter a valid email address",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <input
                  {...field}
                  type="email"
                  placeholder="ईमेल"
                  className={`w-full px-3 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm font-medium transition-all duration-300 bg-[var(--bg-surface)] text-[var(--text-primary)] ${
                    error
                      ? "border-red-400 focus:border-red-400 focus:ring-red-100"
                      : "border-[var(--border-light)] hover:border-[var(--border-medium)]"
                  }`}
                />
                {field.value && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <svg
                      className="w-4 h-4 text-green-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
                {error && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {error.message}
                  </div>
                )}
              </>
            )}
          />
        </div>
      )}
      {formData.loginMethod === "phone" && (
        <div className="relative">
          <Controller
            name="phone"
            control={control}
            rules={{
              required:
                t("auth.registration.phoneRequired") ||
                "Phone number is required",
              pattern: {
                value: /^[6-9]\d{9}$/,
                message:
                  t("auth.registration.invalidPhone") ||
                  "Please enter a valid 10-digit phone number",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <input
                  {...field}
                  type="tel"
                  placeholder="फ़ोन नंबर"
                  className={`w-full px-3 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm font-medium transition-all duration-300 bg-[var(--bg-surface)] text-[var(--text-primary)] ${
                    error
                      ? "border-red-400 focus:border-red-400 focus:ring-red-100"
                      : "border-[var(--border-light)] hover:border-[var(--border-medium)]"
                  }`}
                />
                {field.value && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <svg
                      className="w-4 h-4 text-green-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
                {error && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {error.message}
                  </div>
                )}
              </>
            )}
          />
        </div>
      )}
      {formData.loginMethod === "aadhar" && (
        <div className="relative">
          <Controller
            name="aadhar"
            control={control}
            rules={{
              required:
                t("auth.registration.aadhaarRequired") ||
                "Aadhaar number is required",
              pattern: {
                value: /^\d{12}$/,
                message:
                  t("auth.registration.invalidAadhaar") ||
                  "Aadhaar must be exactly 12 digits",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <input
                  {...field}
                  type="text"
                  maxLength={12}
                  placeholder="आधार नंबर"
                  className={`w-full px-3 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm font-medium transition-all duration-300 bg-[var(--bg-surface)] text-[var(--text-primary)] ${
                    error
                      ? "border-red-400 focus:border-red-400 focus:ring-red-100"
                      : "border-[var(--border-light)] hover:border-[var(--border-medium)]"
                  }`}
                />
                {field.value && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <svg
                      className="w-4 h-4 text-green-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
                {error && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {error.message}
                  </div>
                )}
              </>
            )}
          />
        </div>
      )}
    </div>
  );
};
